import React, { useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useLoaderData, useLocation } from "react-router";
import Select from "react-select";

import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";

import { selectUsers } from "@features/user/userSlice";

import { useAppSelector } from "@hooks/reduxHooks";

import { displayFormAlert, getUserFromLocalStorage } from "@lib/utils";

import AdminControls from "@LeagueManagement/Organizations/components/AdminControls";
import DeleteOrganizationModal from "@LeagueManagement/Organizations/components/DeleteOrganizationModal";
import { useOrganizationsContext } from "@LeagueManagement/Organizations/contexts/OrganizationsContext";
import useOrganizationHandlers from "@LeagueManagement/Organizations/hooks/useOrganizationHandlers";
import orgFormSchema from "@LeagueManagement/Organizations/lib/orgFormSchema";
import { ILeagueMgmtOrgInfoData } from "@LeagueManagement/Organizations/types/organizations";

const Organization = (): React.JSX.Element => {
    const location = useLocation();
    const isOnLeagueManagementPage = /league-management/.test(
        location.pathname,
    );
    const isAddingOrg = /organizations\/add\/?$/.test(location.pathname);
    const [show, setShow] = useState<boolean>(false);

    const users = useAppSelector(selectUsers);
    const user = getUserFromLocalStorage();

    const { teamsList, organizations } = useOrganizationsContext();

    const { orgId } = useLoaderData() as {
        orgId?: string;
    };

    const organization = organizations?.find(
        o => o.id === parseInt(orgId || ""),
    );

    const methods = useForm<ILeagueMgmtOrgInfoData>({
        mode: "onChange",
        resolver: yupResolver(orgFormSchema),
        defaultValues: {
            name: organization?.name || "",
            primaryUser: organization
                ? {
                      value: organization?.primaryUser.id,
                      label: `${organization?.primaryUser.name} (${organization?.primaryUser.email})`,
                  }
                : undefined,
            teams: organization
                ? organization.teams.map(t => ({
                      value: t.id,
                      label: `${t.name} ${t.ageLevel} - (${t.coach.name} - ${t.coach.email})`,
                  }))
                : [],
            approved: organization?.approved === 1 ? true : false,
        },
    });

    const watchTeams = methods.watch("teams");
    const isOrgApproved = methods.watch(
        "approved",
        organization?.approved === 1 ? true : false,
    );

    const { handleSubmit, handleDelete } = useOrganizationHandlers({
        isAddingOrg,
        organization,
    });

    return (
        <section className="orgInfo">
            <FormProvider {...methods}>
                <Form
                    onSubmit={methods.handleSubmit(handleSubmit)}
                    id="leagueMgmtOrgInfoForm"
                >
                    <Row>
                        <Form.Group controlId="name" as={Col} md={5}>
                            <Form.Label>Organization Name</Form.Label>
                            <Form.Control
                                type="text"
                                {...methods.register("name", {
                                    value: organization?.name,
                                })}
                                aria-invalid={
                                    methods.formState.errors.name
                                        ? "true"
                                        : "false"
                                }
                            />
                            {methods.formState.errors.name &&
                                displayFormAlert({
                                    level: "danger",
                                    text: methods.formState.errors.name
                                        .message as string,
                                })}
                        </Form.Group>
                        {user!.userLevel! === 4 &&
                            organization?.approved === 0 && (
                                <Col md={7}>
                                    <h4
                                        style={{
                                            color: "#f00",
                                            marginTop: "2em",
                                            textAlign: "center",
                                        }}
                                    >
                                        Organizational changes pending admin
                                        approval
                                    </h4>
                                </Col>
                            )}
                        {!isAddingOrg && user!.userLevel! <= 2 && (
                            <AdminControls
                                methods={methods}
                                organization={organization}
                                isOrgApproved={isOrgApproved}
                            />
                        )}
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group controlId="primaryUser">
                                <Form.Label>Organization User</Form.Label>
                                <Controller
                                    name="primaryUser"
                                    control={methods.control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            key={JSON.stringify(field.value)}
                                            isSearchable={true}
                                            options={users
                                                .filter(u =>
                                                    user!.userLevel! > 2
                                                        ? u.organization ===
                                                          organization?.id
                                                        : true,
                                                )
                                                .map(u => ({
                                                    value: parseInt(
                                                        u.id.toString(),
                                                    ),
                                                    label: `${u.meta.firstName} ${u.meta.lastName} (${u.email})`,
                                                }))}
                                        />
                                    )}
                                />
                                {methods.formState.errors.primaryUser &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors
                                            .primaryUser.label
                                            ?.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Form.Group controlId="teams" as={Col}>
                            <Form.Label>
                                Teams (
                                {watchTeams
                                    ? watchTeams.length
                                    : organization
                                      ? organization?.teams.length
                                      : 0}
                                )
                            </Form.Label>
                            <Controller
                                name="teams"
                                control={methods.control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        isMulti
                                        isSearchable={true}
                                        options={teamsList}
                                    />
                                )}
                            />
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={
                                    user!.userLevel! === 4 &&
                                    organization?.approved === 0
                                }
                            >
                                {isAddingOrg
                                    ? "Add Organization"
                                    : "Edit Organization"}
                            </Button>
                            {!isAddingOrg && isOnLeagueManagementPage && (
                                <Button
                                    type="button"
                                    variant="danger"
                                    className={classNames("mx-2")}
                                    onClick={() => setShow(true)}
                                >
                                    Delete Organization
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Form>
            </FormProvider>
            {!isAddingOrg && (
                <DeleteOrganizationModal
                    show={show}
                    setShow={setShow}
                    organization={organization}
                    handleDelete={handleDelete}
                />
            )}
        </section>
    );
};

export default Organization;
